import { useEffect, useMemo, useState } from "react";
import { Building2, Loader2, Plus, Search, Sparkles, Trash2, UserPlus, UserX, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { ROLES, useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60);
}

function StatusBadge({ org }) {
  if (org.deleted_at) return <span className="gc-badge bg-rose-50 text-rose-700 border border-rose-200">Terhapus</span>;
  if (org.is_active === false) return <span className="gc-badge bg-slate-100 text-slate-600 border border-slate-200">Nonaktif</span>;
  if (org.is_demo) return <span className="gc-badge bg-amber-50 text-amber-800 border border-amber-200">Data Demo</span>;
  return <span className="gc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Aktif</span>;
}

export default function Organizations() {
  const { user, refresh } = useAuth();
  const isSuperAdmin = user?.platform_role === "super_admin";

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Create org
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "", slug: "", address: "", email: "", phone: "", responsible_person: "",
  });
  const [creating, setCreating] = useState(false);

  // Delete confirm
  const [delOrg, setDelOrg] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Members management
  const [memOrg, setMemOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "operator", password: "" });
  const [inviting, setInviting] = useState(false);

  const loadOrgs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations");
      setOrgs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrgs(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => {
      if (!q) return true;
      return [o.name, o.slug, o.email, o.responsible_person, o.address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [orgs, search]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = {
        ...createForm,
        slug: createForm.slug || slugify(createForm.name),
        is_partner: true,
      };
      await api.post("/organizations", body);
      toast.success(`Perusahaan "${body.name}" berhasil dibuat.`);
      setCreateOpen(false);
      setCreateForm({ name: "", slug: "", address: "", email: "", phone: "", responsible_person: "" });
      await loadOrgs();
      await refresh();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    if (!delOrg) return;
    setDeleting(true);
    try {
      await api.delete(`/organizations/${delOrg.id}`);
      toast.success(`Perusahaan "${delOrg.name}" dinonaktifkan.`);
      setDelOrg(null);
      await loadOrgs();
      await refresh();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setDeleting(false);
    }
  };

  const openMembers = async (org) => {
    setMemOrg(org);
    setMembers([]);
    setMembersLoading(true);
    try {
      const { data } = await api.get(`/organizations/${org.id}/members`);
      setMembers(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setMembersLoading(false);
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!memOrg) return;
    setInviting(true);
    try {
      await api.post(`/organizations/${memOrg.id}/invitations`, inviteForm);
      toast.success(`Anggota "${inviteForm.email}" ditambahkan.`);
      setInviteForm({ email: "", name: "", role: "operator", password: "" });
      const { data } = await api.get(`/organizations/${memOrg.id}/members`);
      setMembers(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/organizations/${memOrg.id}/members/${m.id}`, { role });
      const { data } = await api.get(`/organizations/${memOrg.id}/members`);
      setMembers(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Hapus anggota "${m.name || m.email}" dari ${memOrg?.name}?`)) return;
    try {
      await api.delete(`/organizations/${memOrg.id}/members/${m.id}`);
      toast.success("Anggota dihapus.");
      const { data } = await api.get(`/organizations/${memOrg.id}/members`);
      setMembers(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center text-slate-600">
        Halaman ini hanya untuk <b>Super Administrator</b>.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organizations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            <Sparkles className="w-3.5 h-3.5" /> Kontrol Super Admin
          </div>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">Perusahaan Pengguna</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Kelola seluruh perusahaan pengguna (mitra industri & demo) di sistem GREENCON HPP.
            Tambah perusahaan baru, kelola anggotanya, atau nonaktifkan perusahaan yang sudah tidak
            aktif. Sistem dimiliki dan dikelola oleh Politeknik Negeri Manado.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="gc-btn-primary" data-testid="btn-create-org">
          <Plus className="w-4 h-4" /> Tambah Perusahaan
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama, slug, penanggung jawab…"
          className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
          data-testid="org-search-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat perusahaan…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-300 rounded-2xl bg-white">
          <Building2 className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="mt-2 text-sm text-slate-600">Belum ada perusahaan cocok.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="org-grid">
          {filtered.map((o) => (
            <article
              key={o.id}
              className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-emerald-400 hover:shadow-sm transition-all"
              data-testid={`org-card-${o.slug}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900 truncate">{o.name}</h3>
                    <StatusBadge org={o} />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">{o.slug}</div>
                  {o.responsible_person && (
                    <div className="text-xs text-slate-600 mt-1">
                      <span className="text-slate-400">PIC:</span> {o.responsible_person}
                    </div>
                  )}
                  {o.email && <div className="text-xs text-slate-600">{o.email}</div>}
                  {o.address && <div className="text-xs text-slate-500 truncate">{o.address}</div>}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => openMembers(o)}
                  disabled={!!o.deleted_at}
                  className="gc-btn-ghost text-xs disabled:opacity-50"
                  data-testid={`btn-manage-members-${o.slug}`}
                >
                  <Users className="w-3.5 h-3.5" /> Kelola Anggota
                </button>
                <button
                  onClick={() => setDelOrg(o)}
                  disabled={!!o.deleted_at}
                  className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-rose-700 hover:bg-rose-50 border border-rose-200 disabled:opacity-40"
                  data-testid={`btn-delete-org-${o.slug}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Nonaktifkan
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Create Org Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!creating) setCreateOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-create-org">
          <DialogHeader><DialogTitle className="font-cabinet">Tambah Perusahaan Baru</DialogTitle></DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <label className="gc-label block mb-1.5">Nama Perusahaan *</label>
              <input required className="gc-input" value={createForm.name}
                     onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                     data-testid="org-form-name" />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Slug (opsional, otomatis dari nama)</label>
              <input className="gc-input font-mono text-sm" value={createForm.slug}
                     onChange={(e) => setCreateForm({ ...createForm, slug: slugify(e.target.value) })}
                     placeholder={slugify(createForm.name) || "contoh-slug"}
                     data-testid="org-form-slug" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="gc-label block mb-1.5">Email PIC</label>
                <input type="email" className="gc-input" value={createForm.email}
                       onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                       data-testid="org-form-email" />
              </div>
              <div>
                <label className="gc-label block mb-1.5">Telepon</label>
                <input className="gc-input" value={createForm.phone}
                       onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                       data-testid="org-form-phone" />
              </div>
            </div>
            <div>
              <label className="gc-label block mb-1.5">Penanggung Jawab</label>
              <input className="gc-input" value={createForm.responsible_person}
                     onChange={(e) => setCreateForm({ ...createForm, responsible_person: e.target.value })}
                     data-testid="org-form-pic" />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Alamat</label>
              <textarea className="gc-input" rows={2} value={createForm.address}
                        onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                        data-testid="org-form-address" />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setCreateOpen(false)} className="gc-btn-ghost">Batal</button>
              <button type="submit" disabled={creating} className="gc-btn-primary" data-testid="org-form-submit">
                {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Menyimpan…</> : <>Simpan</>}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Org Dialog */}
      <Dialog open={!!delOrg} onOpenChange={(o) => { if (!deleting && !o) setDelOrg(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-delete-org">
          <DialogHeader><DialogTitle className="font-cabinet text-rose-700">Nonaktifkan Perusahaan</DialogTitle></DialogHeader>
          {delOrg && (
            <div className="text-sm text-slate-700 space-y-2">
              <p>
                Perusahaan <b>{delOrg.name}</b> akan <b>dinonaktifkan</b> beserta seluruh keanggotaannya.
                Data master (produk, mix design, batch, HPP) tetap tersimpan sebagai arsip.
              </p>
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-amber-800">
                Tindakan ini hanya soft-delete — hubungi tim teknis jika ingin mengembalikan akses.
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDelOrg(null)} disabled={deleting} className="gc-btn-ghost">Batal</button>
            <button onClick={confirmDelete} disabled={deleting}
                    className="h-9 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-2"
                    data-testid="btn-confirm-delete-org">
              {deleting ? <><Loader2 className="w-4 h-4 animate-spin" /> Menghapus…</> : <><Trash2 className="w-4 h-4" /> Nonaktifkan</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Manager Dialog */}
      <Dialog open={!!memOrg} onOpenChange={(o) => { if (!o) setMemOrg(null); }}>
        <DialogContent className="max-w-2xl" data-testid="dialog-manage-members">
          <DialogHeader>
            <DialogTitle className="font-cabinet flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" /> Kelola Anggota — {memOrg?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Invite form */}
          <form onSubmit={submitInvite} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end border-b border-slate-100 pb-3">
            <div className="md:col-span-2">
              <label className="gc-label block mb-1">Nama</label>
              <input required className="gc-input h-9 text-sm" value={inviteForm.name}
                     onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                     data-testid="mem-invite-name" />
            </div>
            <div className="md:col-span-2">
              <label className="gc-label block mb-1">Email</label>
              <input required type="email" className="gc-input h-9 text-sm" value={inviteForm.email}
                     onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                     data-testid="mem-invite-email" />
            </div>
            <div>
              <label className="gc-label block mb-1">Peran</label>
              <select className="gc-input h-9 text-sm" value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                      data-testid="mem-invite-role">
                {Object.entries(ROLES).filter(([k]) => k !== "super_admin").map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="gc-label block mb-1">Kata Sandi</label>
              <input required className="gc-input h-9 text-sm" value={inviteForm.password}
                     onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                     placeholder="min 6 karakter" data-testid="mem-invite-password" />
            </div>
            <div className="md:col-span-6 flex justify-end">
              <button type="submit" disabled={inviting} className="gc-btn-primary text-sm" data-testid="mem-invite-submit">
                {inviting ? <><Loader2 className="w-4 h-4 animate-spin" /> Menambah…</> : <><UserPlus className="w-4 h-4" /> Tambah Anggota</>}
              </button>
            </div>
          </form>

          {/* Members list */}
          {membersLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Memuat anggota…
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500">Belum ada anggota.</div>
          ) : (
            <div className="max-h-80 overflow-auto -mx-1">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left py-2 px-2 font-bold">Nama / Email</th>
                    <th className="text-left py-2 px-2 font-bold">Peran</th>
                    <th className="text-left py-2 px-2 font-bold">Status</th>
                    <th className="text-right py-2 px-2 font-bold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-slate-50" data-testid={`mem-row-${m.email}`}>
                      <td className="py-2 px-2">
                        <div className="font-medium text-slate-900">{m.name}</div>
                        <div className="text-xs text-slate-500">{m.email}</div>
                      </td>
                      <td className="py-2 px-2">
                        <select value={m.role} onChange={(e) => changeRole(m, e.target.value)}
                                className="gc-input h-8 text-xs min-w-[160px]"
                                data-testid={`mem-role-${m.email}`}>
                          {Object.entries(ROLES).filter(([k]) => k !== "super_admin").map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 px-2">
                        {m.is_active ? (
                          <span className="gc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Aktif</span>
                        ) : (
                          <span className="gc-badge bg-slate-100 text-slate-600 border border-slate-200">Nonaktif</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <button onClick={() => removeMember(m)}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold text-rose-700 hover:bg-rose-50 border border-rose-200"
                                data-testid={`mem-remove-${m.email}`}>
                          <UserX className="w-3 h-3" /> Hapus
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setMemOrg(null)} className="gc-btn-ghost">Tutup</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
